import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Lock,
  CheckCircle,
  Users,
  MessageSquare,
  Package,
  CreditCard,
  ArrowRight,
  Smartphone,
  Monitor,
  Truck,
  Handshake,
  AlertCircle,
  FileText,
  Clock,
  HelpCircle,
  Leaf
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const Escrow = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeSection, setActiveSection] = useState('for-buyers');

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  const buyerGuide = [
    {
      step: 1,
      title: "Agree on Terms",
      description: "Negotiate price and conditions with seller",
      action: "Use our in-app chat to discuss details",
      icon: <MessageSquare className="w-6 h-6" />
    },
    {
      step: 2,
      title: "Select Escrow Payment",
      description: "Choose 'Pay with Escrow' at checkout",
      action: "Click the escrow option instead of direct payment",
      icon: <CreditCard className="w-6 h-6" />
    },
    {
      step: 3,
      title: "Funds Secured",
      description: "Your payment is held safely by EcoMart",
      action: "Wait for seller to confirm order acceptance",
      icon: <Lock className="w-6 h-6" />
    },
    {
      step: 4,
      title: "Receive & Inspect",
      description: "Get your item and check it thoroughly",
      action: "Take your time to verify condition and authenticity",
      icon: <Package className="w-6 h-6" />
    },
    {
      step: 5,
      title: "Release Funds",
      description: "Confirm satisfaction to pay seller",
      action: "Click 'Confirm Receipt' in your orders",
      icon: <CheckCircle className="w-6 h-6" />
    }
  ];

  const sellerGuide = [
    {
      step: 1,
      title: "Enable Escrow",
      description: "Ask buyers to make payment into Escrow",
      action: "Add escrow payment to your listings description",
      icon: <Shield className="w-6 h-6" />
    },
    {
      step: 2,
      title: "Prepare & Deliver",
      description: "Ship or arrange meetup with buyer",
      action: "Update delivery status and provide tracking",
      icon: <Truck className="w-6 h-6" />
    },
    {
      step: 3,
      title: "Receive Payment",
      description: "Get paid after buyer confirms satisfaction",
      action: "Funds released to your account automatically",
      icon: <CheckCircle className="w-6 h-6" />
    }
  ];

  const escrowScenarios = [
    {
      title: "High-Value Items",
      description: "Electronics, jewelry, collectibles",
      icon: <Monitor className="w-8 h-8" />,
      tips: [
        "Always use escrow for items",
        "Get detailed photos and serial numbers",
        "Meet in safe, public locations"
      ]
    },
    {
      title: "Services & Freelance",
      description: "Design, repairs, consulting work",
      icon: <FileText className="w-8 h-8" />,
      tips: [
        "Break projects into milestones",
        "Set clear deliverables",
        "Use escrow for each milestone"
      ]
    },
    {
      title: "Remote Transactions",
      description: "Shipping to different cities/states",
      icon: <Smartphone className="w-8 h-8" />,
      tips: [
        "Require tracking numbers",
        "Take photos of packaging",
        "Keep all communication in-app"
      ]
    },
    {
      title: "First-Time Transactions",
      description: "Dealing with new users",
      icon: <Users className="w-8 h-8" />,
      tips: [
        "Build trust gradually",
        "Start with smaller transactions",
        "Verify user ratings first"
      ]
    }
  ];

  

  const handleStartEscrow = () => {
    if (user) {
      navigate('/chats', { state: { useEscrow: true } });
    } else {
      navigate('/login', { state: { redirectTo: '/chats' } });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
               style={{ backgroundColor: brandColorLight }}>
            <div className="flex items-center">
              <Shield className="w-8 h-8 ml-1" style={{ color: brandColor }} />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            EcoMart Escrow Protection
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Trade with confidence. Funds are held securely until both parties are satisfied.
          </p>
        </div>

        {/* What is Escrow */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Escrow?</h2>
              <p className="text-gray-600 mb-4">
                EcoMart Escrow acts as a trusted third party that securely holds the buyer's 
                payment until the transaction is successfully completed. This protects both 
                buyers and sellers by ensuring:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Buyers</h4>
                    <p className="text-sm text-gray-600">Get exactly what you paid for before releasing funds</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Lock className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Sellers</h4>
                    <p className="text-sm text-gray-600">Receive guaranteed payment for delivered goods</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-1/3">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
                       style={{ backgroundColor: brandColorLight }}>
                    <AlertCircle className="w-6 h-6" style={{ color: brandColor }} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Important</h3>
                  <p className="text-sm text-gray-600">
                    Never send direct payments. Always use escrow for protected transactions.
                  </p>
                </div>
                <button
                  onClick={handleStartEscrow}
                  className="w-full py-3 rounded-lg font-semibold transition-all hover:shadow-md"
                  style={{ 
                    backgroundColor: brandColor,
                    color: 'white'
                  }}
                >
                  Start Using Escrow
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-2 mb-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveSection('for-buyers')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeSection === 'for-buyers'
                  ? 'text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: activeSection === 'for-buyers' ? brandColor : 'transparent'
              }}
            >
              For Buyers
            </button>
            <button
              onClick={() => setActiveSection('for-sellers')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeSection === 'for-sellers'
                  ? 'text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: activeSection === 'for-sellers' ? brandColor : 'transparent'
              }}
            >
              For Sellers
            </button>
            <button
              onClick={() => setActiveSection('when-to-use')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeSection === 'when-to-use'
                  ? 'text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: activeSection === 'when-to-use' ? brandColor : 'transparent'
              }}
            >
              When to Use
            </button>
          </div>
        </div>

        {/* Buyers Section */}
        {activeSection === 'for-buyers' && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How Buyers Use Escrow</h2>
              
              <div className="space-y-6">
                {buyerGuide.map((step) => (
                  <div key={step.step} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                           style={{ backgroundColor: brandColorLight }}>
                        <div style={{ color: brandColor }}>{step.icon}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 text-white rounded-full text-sm font-bold mr-3"
                              style={{ backgroundColor: brandColor }}>
                          {step.step}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 mb-2">{step.description}</p>
                      <div className="bg-gray-50 rounded-lg p-3 inline-block">
                        <span className="text-sm font-medium text-gray-700">📋 Action: {step.action}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 rounded-xl"
                   style={{ backgroundColor: brandColorLight }}>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: brandColor }} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Buyer Protection Benefits</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-3"></div>
                        <span>Funds held securely until you confirm satisfaction</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-3"></div>
                        <span>Professional dispute resolution if needed</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Start for Buyers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
              
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h3>
                <p className="text-gray-600 mb-4">
                  Our buyer support team can guide you through your first escrow transaction.
                </p>
                <button
                  onClick={() => navigate('/support')}
                  className="w-full py-3 rounded-lg font-semibold transition-all hover:shadow-sm"
                  style={{ 
                    backgroundColor: brandColorLight,
                    color: brandColor
                  }}
                >
                  Contact Buyer Support
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sellers Section */}
        {activeSection === 'for-sellers' && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How Sellers Use Escrow</h2>
              
              <div className="space-y-6">
                {sellerGuide.map((step) => (
                  <div key={step.step} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                           style={{ backgroundColor: brandColorLight }}>
                        <div style={{ color: brandColor }}>{step.icon}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 text-white rounded-full text-sm font-bold mr-3"
                              style={{ backgroundColor: brandColor }}>
                          {step.step}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 mb-2">{step.description}</p>
                      <div className="bg-gray-50 rounded-lg p-3 inline-block">
                        <span className="text-sm font-medium text-gray-700">📋 Action: {step.action}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 rounded-xl"
                   style={{ backgroundColor: brandColorLight }}>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 mt-1 flex-shrink-0" style={{ color: brandColor }} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Seller Benefits</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-3"></div>
                        <span>Payment secured before you ship or deliver</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-3"></div>
                        <span>Builds trust with new customers</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-3"></div>
                        <span>Eliminates payment disputes and chargebacks</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Seller Setup */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Seller Tips</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Always communicate through EcoMart chat</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Take photos/videos of packaging process</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Update delivery status promptly</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* When to Use Section */}
        {activeSection === 'when-to-use' && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">When to Use Escrow</h2>
              <p className="text-gray-600 mb-8">
                Use EcoMart Escrow in these situations to protect yourself and build trust with trading partners.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {escrowScenarios.map((scenario, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start mb-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                           style={{ backgroundColor: brandColorLight }}>
                        <div style={{ color: brandColor }}>{scenario.icon}</div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{scenario.title}</h3>
                        <p className="text-gray-600 text-sm">{scenario.description}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {scenario.tips.map((tip, tipIndex) => (
                        <div key={tipIndex} className="flex items-start">
                          <div className="w-2 h-2 rounded-full bg-orange-500 mr-3 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700 text-sm">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Decision Guide */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Escrow Decision Guide</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-5 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                       style={{ backgroundColor: brandColorLight }}>
                    <span className="font-bold" style={{ color: brandColor }}>✓</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Use Escrow For:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>High-value items</li>
                    <li>First-time transactions</li>
                    <li>Remote/shipped items</li>
                    <li>Services & freelance work</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        

        {/* Final CTA */}
        <div className="mt-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white text-center">
          <div className="max-w-2xl mx-auto">
            <Shield className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">Start Trading with Confidence</h2>
            <p className="mb-8 text-lg opacity-95">
              EcoMart Escrow makes buying and selling safe for everyone. 
              No more payment worries, no more delivery doubts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleStartEscrow}
                className="px-8 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center"
              >
                <Shield className="w-5 h-5 mr-2" />
                Use Escrow Now
              </button>
            
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default Escrow;